import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { Plus, Search, Calendar as CalendarIcon, RefreshCw, AlertCircle, Clock, Tag, ChevronRight, X } from "lucide-react";
import { api } from "../lib/api";
import AppHeader from "../components/AppHeader";
import CRMTabs from "../components/CRMTabs";

const STATUSES = [
  { key: "sin_empezar", label: "Sin empezar", color: "bg-stone-600", accent: "border-stone-500" },
  { key: "en_progreso", label: "En progreso", color: "bg-amber-700", accent: "border-amber-500" },
  { key: "preparado_subasta", label: "Preparado para subasta", color: "bg-[#E0651F]", accent: "border-[#E0651F]" },
  { key: "pendiente_gestion", label: "Pendiente de gestión", color: "bg-emerald-700", accent: "border-emerald-500" },
  { key: "terminado", label: "Terminados", color: "bg-stone-800", accent: "border-stone-600" },
];

const PRIORITY_DOT = {
  baja: "bg-stone-400",
  media: "bg-amber-400",
  alta: "bg-red-500",
};

export default function CRM() {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [error, setError] = useState("");
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const fetchCards = async (opts = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (opts.search) params.set("search", opts.search);
      if (opts.tag) params.set("tag", opts.tag);
      const { data } = await api.get(`/admin/crm/cards?${params.toString()}`);
      setCards(data.cards || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Error cargando CRM");
    } finally {
      setLoading(false);
    }
  };

  const sync = async () => {
    try {
      await api.post("/admin/crm/sync");
      fetchCards({ search, tag: tagFilter });
    } catch (e) {
      setError("Error al sincronizar");
    }
  };

  useEffect(() => { fetchCards(); }, []);

  const tags = useMemo(() => {
    const s = new Set();
    cards.forEach((c) => (c.tags || []).forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [cards]);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(STATUSES.map((s) => [s.key, []]));
    cards.forEach((c) => {
      const k = STATUSES.some((s) => s.key === c.status) ? c.status : "sin_empezar";
      g[k].push(c);
    });
    return g;
  }, [cards]);

  const onDragEnd = async ({ active, over }) => {
    if (!over) return;
    const targetStatus = over.id;
    const card = cards.find((c) => c.id === active.id);
    if (!card || card.status === targetStatus) return;
    // Optimistic update
    setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, status: targetStatus } : c)));
    try {
      await api.patch(`/admin/crm/cards/${card.id}`, { status: targetStatus });
    } catch (e) {
      // Rollback on failure
      setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, status: card.status } : c)));
      setError("No se pudo cambiar el estado");
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <AppHeader />

      <header className="border-b border-stone-800 px-4 sm:px-6 py-4 sm:py-5 bg-stone-900">
        <div className="max-w-[1600px] mx-auto flex flex-col lg:flex-row lg:flex-wrap lg:items-center gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F]">CRM</div>
            <h1 className="font-display text-xl sm:text-2xl lg:text-3xl font-black tracking-tight text-white">Tablero de tareas</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap w-full lg:w-auto">
            <div className="relative flex-1 min-w-[140px] sm:flex-initial sm:w-56">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchCards({ search, tag: tagFilter })}
                placeholder="Buscar..."
                className="w-full bg-stone-800 border border-stone-700 text-stone-100 pl-9 pr-3 py-2 text-sm rounded-sm focus:border-[#E0651F] focus:outline-none"
                data-testid="crm-search"
              />
            </div>
            <select
              value={tagFilter}
              onChange={(e) => { setTagFilter(e.target.value); fetchCards({ search, tag: e.target.value }); }}
              className="bg-stone-800 border border-stone-700 text-stone-100 px-2 sm:px-3 py-2 text-xs sm:text-sm rounded-sm focus:outline-none max-w-[140px] sm:max-w-none"
              data-testid="crm-tag-filter"
            >
              <option value="">Todas las etiquetas</option>
              {tags.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <button onClick={sync}
              className="inline-flex items-center gap-1.5 bg-stone-800 hover:bg-stone-700 border border-stone-700 text-stone-100 px-2.5 sm:px-3 py-2 text-[10px] sm:text-xs uppercase tracking-[0.15em] font-bold rounded-sm"
              data-testid="crm-sync-btn">
              <RefreshCw className="w-4 h-4" /> <span className="hidden sm:inline">Sincronizar</span>
            </button>
            <button onClick={() => navigate("/admin/crm/nueva")}
              className="inline-flex items-center gap-1.5 bg-[#E0651F] hover:bg-[#ff7a30] text-white px-3 sm:px-4 py-2 text-[10px] sm:text-xs uppercase tracking-[0.15em] font-bold rounded-sm ml-auto sm:ml-0"
              data-testid="crm-new-btn">
              <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Nueva tarjeta</span><span className="sm:hidden">Nueva</span>
            </button>
          </div>
        </div>
      </header>

      <CRMTabs />

      {error && (
        <div className="bg-red-900/50 text-red-200 border-y border-red-700 px-6 py-2 text-sm">{error}</div>
      )}

      <main className="px-4 sm:px-6 py-4 sm:py-6 overflow-x-auto">
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <div className="flex gap-3 sm:gap-4 min-h-[calc(100vh-220px)]">
            {STATUSES.map((s) => (
              <Column key={s.key} status={s} cards={grouped[s.key] || []} />
            ))}
          </div>
        </DndContext>
        {loading && <div className="text-center text-stone-500 mt-8 text-sm">Cargando…</div>}
      </main>
    </div>
  );
}

function Column({ status, cards }) {
  const { setNodeRef, isOver } = useDroppable({ id: status.key });
  const isTerminated = status.key === "terminado";
  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-72 sm:w-80 bg-stone-900 border border-stone-800 rounded-sm transition-colors ${isOver ? "border-[#E0651F] bg-stone-900/80" : ""} ${isTerminated ? "opacity-90" : ""}`}
      data-testid={`crm-column-${status.key}`}
    >
      <div className="px-4 py-3 border-b border-stone-800 flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${status.color}`} />
        <span className="text-xs tracking-[0.18em] uppercase font-bold text-stone-300">{status.label}</span>
        <span className="ml-auto text-xs text-stone-500 font-mono">{cards.length}</span>
      </div>
      {isTerminated && (
        <div className="px-4 py-2 bg-stone-800/40 border-b border-stone-800 text-[10px] text-stone-500 leading-relaxed">
          Se auto-eliminan a los 30 días. El cliente permanece en la base de datos.
        </div>
      )}
      <div className="p-3 space-y-3">
        {cards.length === 0 && (
          <div className="text-xs text-stone-600 italic text-center py-8">Sin tarjetas</div>
        )}
        {cards.map((c) => <Card key={c.id} card={c} accent={status.accent} isTerminated={isTerminated} />)}
      </div>
    </div>
  );
}

function Card({ card, accent, isTerminated }) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: card.id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50 } : undefined;

  const auctionWarn = useMemo(() => {
    if (!card.auction_date) return null;
    try {
      const d = new Date(card.auction_date);
      const diff = (d.getTime() - Date.now()) / (1000 * 60 * 60);
      if (diff < 0) return "past";
      if (diff < 48) return "urgent";
      return "future";
    } catch { return null; }
  }, [card.auction_date]);

  // Días restantes antes de auto-purga (solo si terminada)
  const daysUntilPurge = useMemo(() => {
    if (!isTerminated || !card.terminated_at) return null;
    try {
      const t = new Date(card.terminated_at).getTime();
      const elapsedDays = (Date.now() - t) / (1000 * 60 * 60 * 24);
      return Math.max(0, Math.ceil(30 - elapsedDays));
    } catch { return null; }
  }, [isTerminated, card.terminated_at]);

  const fmtAuction = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }) +
        " " + d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => navigate(`/admin/crm/${card.id}`)}
      className={`bg-stone-800 border-l-2 ${accent} border-y border-r border-stone-700/50 p-3 rounded-sm cursor-pointer hover:bg-stone-700/70 transition-colors ${isDragging ? "opacity-50" : ""} ${isTerminated ? "opacity-60 grayscale" : ""}`}
      data-testid={`crm-card-${card.id}`}
    >
      <div className="flex items-start gap-2 mb-2">
        <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${PRIORITY_DOT[card.priority] || PRIORITY_DOT.media}`} title={`Prioridad ${card.priority}`} />
        <h3 className="font-display font-bold text-sm text-white leading-tight flex-1">{card.title}</h3>
      </div>
      {card.client_name && (
        <div className="text-xs text-stone-300 mb-1.5 truncate">👤 {card.client_name}</div>
      )}
      {card.auction_id && (
        <div className="text-[11px] text-stone-400 font-mono mb-1.5 truncate">{card.auction_id}</div>
      )}
      {card.auction_date && (
        <div className={`text-[11px] flex items-center gap-1 ${auctionWarn === "urgent" ? "text-red-400 font-bold" : auctionWarn === "past" ? "text-stone-500 line-through" : "text-amber-300"}`}>
          {auctionWarn === "urgent" ? <AlertCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
          {fmtAuction(card.auction_date)}
        </div>
      )}
      {isTerminated && daysUntilPurge !== null && (
        <div className="mt-1.5 text-[10px] text-stone-500 italic" data-testid="terminated-purge-countdown">
          Se elimina en {daysUntilPurge} día{daysUntilPurge === 1 ? "" : "s"}
        </div>
      )}
      {card.tags && card.tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {card.tags.slice(0, 3).map((t) => (
            <span key={t} className="text-[9px] tracking-wide uppercase font-bold px-1.5 py-0.5 bg-stone-700 text-stone-300 rounded-sm">{t}</span>
          ))}
        </div>
      )}
      {card.comments && card.comments.length > 0 && (
        <div className="mt-2 text-[10px] text-stone-500">💬 {card.comments.length}</div>
      )}
    </div>
  );
}
